import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

// Components
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import theme from "../../../shared/utils/themeColors";

// Hooks
import { useQuestionCategories } from "../../question-bank/hooks/useQuestionCategories";

// Types
import { QuestionCategory } from "../../question-bank/types/question-bank.types";

// Helper components from question bank (reusing them)
const CategoryItem: React.FC<{
  category: QuestionCategory;
  onSelect: (category: QuestionCategory) => void;
  level?: number;
}> = ({ category, onSelect, level = 0 }) => {
  return (
    <div
      className="flex items-center p-2 my-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
      onClick={() => onSelect(category)}
    >
      <div
        className={`text-xs px-2 py-1 rounded-full mr-2 min-w-6 text-center ${
          theme.bg.capsule
        } ${category.color ? "text-white" : theme.text.primary}`}
        {...(category.color
          ? { style: { backgroundColor: category.color } }
          : {})}
      >
        {category.totalQuestionCount || 0}
      </div>
      <div className="flex-grow">
        <h3
          className={`${
            level === 0
              ? "font-medium text-gray-800 dark:text-gray-100"
              : "text-sm font-medium text-gray-700 dark:text-gray-300"
          }`}
        >
          {category.name}
        </h3>
        {category.description && level < 2 && (
          <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 line-clamp-1">
            {category.description}
          </p>
        )}
      </div>
    </div>
  );
};

const CategoryTree: React.FC<{
  category: QuestionCategory;
  onSelect: (category: QuestionCategory) => void;
  level?: number;
}> = ({ category, onSelect, level = 0 }) => {
  return (
    <div key={category.id}>
      <CategoryItem category={category} onSelect={onSelect} level={level} />

      {category.subcategories && category.subcategories.length > 0 && (
        <div
          className={`mt-1 ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700`}
        >
          {category.subcategories.map((subcategory) => (
            <CategoryTree
              key={subcategory.id}
              category={subcategory}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function TutorialCategoryExplorer() {
  const {
    hierarchy,
    loading: categoriesLoading,
    error: categoriesError,
    refresh,
  } = useQuestionCategories("tutorial");

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Handle category selection
  const handleCategorySelect = useCallback(
    (category: QuestionCategory) => {
      // Navigate to the tutorial list for this category
      navigate(`/tutorial-bank/category/${category.id}`);
    },
    [navigate]
  );

  // Filter categories based on search term
  const filteredCategories = searchTerm
    ? hierarchy.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : hierarchy;

  return (
    <AuthenticatedLayout
      isLoading={categoriesLoading}
      error={categoriesError}
      isEmpty={!categoriesLoading && filteredCategories.length === 0}
      emptyStateProps={{
        title: "No categories found",
        description: searchTerm
          ? "No categories match your search criteria"
          : "No categories available in the tutorial bank",
        icon: (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-8 h-8 ${theme.text.muted}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
              />
            </svg>
          </div>
        ),
      }}
      pageHeader={{
        title: "Tutorial Categories",
        refresh: {
          show: true,
          onRefresh: refresh,
        },
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="">
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Select a category to view tutorials
            </p>
          </div>
          <div className="space-y-1">
            {/* Category Hierarchy */}
            <div className="p-0">
              {filteredCategories && filteredCategories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredCategories.map((mainCategory) => (
                    <div
                      key={mainCategory.id}
                      className="mb-6 p-3 bg-white dark:bg-gray-700 rounded-md"
                    >
                      <CategoryTree
                        category={mainCategory}
                        onSelect={handleCategorySelect}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    No categories available
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}

export default TutorialCategoryExplorer;
